
    // IMPORTS
    import { computed, defineComponent, PropType } from 'vue'
    import router from '@/router'

    import DynamicViewSwitcher from '@/components/common/dynamic-view-switcher.vue'
    import TopBannerToolbar from '@/components/top-banner-toolbar.vue'
    import MyIcon from '@/components/common/my-icon.vue'

    import { DropDownButton, DropDownButtonItemClickEvent } from '@progress/kendo-vue-buttons';
    import { buildLocalization } from '@/util'
    import { INavigationItem } from '@/types/INavigationItem'

    export default defineComponent({
        name: 'navigationBar',

        components: {
            DynamicViewSwitcher,
            DropDownButton,
            TopBannerToolbar,
            MyIcon
        },

        props: {
            items: { type: Array as PropType<INavigationItem[]>, required: false, default: () => [] },
            renderNavigationTemplate: { type: Boolean as PropType<boolean>, required: false, default: () => false }
        },

        setup(props) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = {}

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const visibleItems = computed(() => props.items.filter(item => !item.condition || item.condition()) ?? [])
            const showNavigationBarOnLargeScreen = computed(() => visibleItems.value.length > 0 || props.renderNavigationTemplate)
            const showContextMenuOnSmallScreen = computed(() => visibleItems.value.length > 0 && !props.renderNavigationTemplate)

            const currentRouteName = computed(() => router.currentRoute.value.name)

            const activeMenuItem = computed(() => visibleItems.value.find(item => item.routeName?.toLowerCase() === currentRouteName.value))                

            const activeMenuItemText = computed(() => activeMenuItem.value?.textName != null
                ? localize(activeMenuItem.value.textName)
                : null
            )

            const computedData = {
                visibleItems,
                showNavigationBarOnLargeScreen,
                showContextMenuOnSmallScreen,
                activeMenuItem,
                activeMenuItemText
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function onMenuItemClick(selectedItem: INavigationItem) {
                if (selectedItem.routeName) {
                    // navigate to named route
                    if (selectedItem.replace) {
                        router.replace({ name: selectedItem.routeName })
                    } else {
                        router.push({ name: selectedItem.routeName })
                    }
                }
                else if (selectedItem.routeFunction) {
                    // execute menu item route function
                    selectedItem.routeFunction()
                }
            }

            function onMenuItemSelect(e: DropDownButtonItemClickEvent) {
                const selectedItem = e.item as INavigationItem

                if (selectedItem != null) {
                    onMenuItemClick(selectedItem)
                }
            }

            const publicMethods = {
                localize,
                onMenuItemClick,
                onMenuItemSelect
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
